import { useState, useEffect, useCallback, useRef } from 'react';
import { Users, CheckCircle, Clock, MousePointerClick, Radio } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  LoadingSpinner,
  MetricCard,
  MetricCardsGrid,
  EmptyState,
  Pagination,
  DataTableFilters,
} from '@/shared/components';
import { journeysApi } from './api';
import { JourneyTable } from './components';
import type { Journey, JourneyMetrics, PaginatedJourneys } from './types';

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
}

export function JourneysPage() {
  const [journeys, setJourneys] = useState<Journey[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 5, total: 0, totalPages: 0 });
  const [metrics, setMetrics] = useState<JourneyMetrics>({
    totalJourneys: 0,
    totalConverted: 0,
    totalActive: 0,
    totalEvents: 0,
    avgEvents: 0,
  });
  const [loading, setLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [_error, setError] = useState<string | null>(null);
  const [convertedFilter, setConvertedFilter] = useState<string>('all');
  const [queryValue, setQueryValue] = useState('');
  const debouncedSearch = useDebouncedValue(queryValue, 300);

  const fetchJourneys = useCallback(async (background = false) => {
    if (!background) {
      setLoading(true);
    }
    setError(null);
    try {
      let converted: boolean | null = null;
      if (convertedFilter === 'true') converted = true;
      if (convertedFilter === 'false') converted = false;

      const data: PaginatedJourneys = await journeysApi.list({
        page: pagination.page,
        limit: pagination.limit,
        converted,
        search: debouncedSearch || undefined,
      });
      setJourneys(data.data || []);
      setPagination(data.pagination);
      if (data.metrics) {
        setMetrics(data.metrics);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch journeys');
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  }, [pagination.page, pagination.limit, convertedFilter, debouncedSearch]);

  useEffect(() => {
    fetchJourneys();
  }, [fetchJourneys]);

  // Long polling every 5 seconds
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    pollingRef.current = setInterval(() => {
      fetchJourneys(true);
    }, 5000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [fetchJourneys]);

  const handleConvertedFilterChange = (value: string) => {
    setConvertedFilter(value);
    setPagination((p) => ({ ...p, page: 1 }));
  };

  const handleQueryChange = (value: string) => {
    setQueryValue(value);
  };

  const handleQueryClear = () => {
    setQueryValue('');
    setPagination((p) => ({ ...p, page: 1 }));
  };

  if (isInitialLoad && loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <LoadingSpinner />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <MetricCardsGrid>
        <MetricCard
          title="Total Journeys"
          value={metrics.totalJourneys}
          icon={Users}
          color="default"
        />
        <MetricCard
          title="Converted"
          value={metrics.totalConverted}
          icon={CheckCircle}
          color="success"
          subtitle={
            metrics.totalJourneys > 0
              ? `${((metrics.totalConverted / metrics.totalJourneys) * 100).toFixed(0)}% rate`
              : undefined
          }
        />
        <MetricCard
          title="Active"
          value={metrics.totalActive}
          icon={Clock}
          color="warning"
        />
        <MetricCard
          title="Avg Events"
          value={metrics.avgEvents}
          icon={MousePointerClick}
          color="default"
        />
      </MetricCardsGrid>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Customer Journeys</CardTitle>
              <CardDescription>
                Track customer touchpoints and conversion paths
              </CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 text-xs text-emerald-600">
                <Radio className="h-3 w-3" />
                <span>Live</span>
              </div>
              <span className="text-sm text-muted-foreground">{pagination.total} journeys</span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <DataTableFilters
            searchPlaceholder="Search by name, email, or order #..."
            searchValue={queryValue}
            onSearchChange={handleQueryChange}
            onSearchClear={handleQueryClear}
            filters={[
              {
                key: 'converted',
                label: 'Status',
                options: [
                  { label: 'Converted', value: 'true' },
                  { label: 'Not Converted', value: 'false' },
                ],
                value: convertedFilter,
                onChange: handleConvertedFilterChange,
              },
            ]}
            onClearAll={() => {
              setConvertedFilter('all');
              setQueryValue('');
            }}
          />

          {journeys.length === 0 ? (
            <EmptyState heading="No journeys found">
              <p>Customer journeys will appear here as visitors browse your store.</p>
            </EmptyState>
          ) : (
            <>
              <JourneyTable journeys={journeys} loading={loading} />

              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages || 1}
                onPrevious={() => setPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}
                onNext={() => setPagination((p) => ({ ...p, page: Math.min(p.totalPages || 1, p.page + 1) }))}
                loading={loading}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
